import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createApiClient } from '../api/helpers.ts'
import { env } from '../env.ts'
import { useImpersonation } from './impersonation.ts'

const AccumulationResetTimeApi = createApiClient({ prefixUrl: env.API_CORRECTIONS })

const ACCUMULATION_RESET_TIMES = 'ACCUMULATION_RESET_TIMES'

export const reportsRainAccumulation = (productCode: string | undefined) => !/^(DRS|FMP)/i.test(productCode ?? '')

export const accumulationResetTimesQuery = (partyAccountId?: string) =>
	queryOptions({
		queryKey: [ACCUMULATION_RESET_TIMES, partyAccountId],
		queryFn: () =>
			AccumulationResetTimeApi.get('v1/accumulation-reset-times', { searchParams: { partyAccountId } }).json<
				AccumulationResetTimeConfiguration[]
			>(),
		staleTime: 1000 * 60 * 5,
	})

export const useAccumulationResetOffset = (sourceId?: string) => {
	const partyAccountId = useImpersonation()?.partyAccountId
	return useQuery({
		...accumulationResetTimesQuery(partyAccountId),
		enabled: !!sourceId,
		select: (configs) => configs.find((config) => config.sourceId === sourceId)?.utcOffsetMinutes ?? 0,
	})
}

export const usePutAccumulationResetTime = () => {
	const partyAccountId = useImpersonation()?.partyAccountId
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ sourceId, body }: { sourceId: string; body: AccumulationResetTimeRequest }) =>
			AccumulationResetTimeApi.put(`v1/accumulation-reset-times/${encodeURIComponent(sourceId)}`, {
				json: body,
				searchParams: { partyAccountId },
				// A newly registered device is not known to this API for a few seconds
				retry: { limit: 3, statusCodes: [404] },
			}),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: [ACCUMULATION_RESET_TIMES, partyAccountId] })
		},
	})
}

type AccumulationResetTimeRequest = { utcOffsetMinutes: number }

export type AccumulationResetTimeConfiguration = AccumulationResetTimeRequest & {
	sourceId: string
	updatedAt?: string
}

const MINUTES_PER_DAY = 24 * 60

// Accumulation resets at local midnight, so the UTC reset time is midnight minus the offset. Returns HH:mm
export const utcOffsetToResetTime = (utcOffsetMinutes: number) => {
	const minutesOfDay = ((-utcOffsetMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
	const hours = Math.floor(minutesOfDay / 60)
	const minutes = minutesOfDay % 60
	return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

// Inverse of utcOffsetToResetTime, normalized to the API's valid range of -720 to 840
export const resetTimeToUtcOffset = (resetTime: string) => {
	const [hours = 0, minutes = 0] = resetTime.split(':').map(Number)
	const offset = 0 - (hours * 60 + minutes)
	return offset < -720 ? offset + MINUTES_PER_DAY : offset
}
